import { LocaleLanguage } from '@/enums/LocaleLanguage';
import { PosLanguage } from '@/enums/PosLanguage';
import UserError from '@/error';
import i18n from '@/i18n';
import CallMessage from '@/interfaces/CallMessage';
import Menu from '@/interfaces/Menu';
import MenuGroup from '@/interfaces/MenuGroup';
import ResponseEvent from '@/interfaces/ResponseEvent';
import ResponseEventItem from '@/interfaces/ResponseEventItem';
import ResponseItem from '@/interfaces/ResponseItem';
import SetMenu from '@/interfaces/SetMenu';
import SetMenuGroup from '@/interfaces/SetMenuGroup';
import Topping from '@/interfaces/Topping';
import ToppingGroup from '@/interfaces/ToppingGroup';
import { logger } from '@/logger';
import { useCommonStore } from '@/stores/common';
import { useConfigStore } from '@/stores/config';
import dayjs from 'dayjs';

/**
 * px 단위를 vw 단위로 전환
 * 1340은 XD로 디자인 된 스크린 너비 기준이지만,
 * 정확한 계산을 위해 지정된 스크린 기준으로 재계산
 * @param value
 */
export const convertPxToVw = (value: number) => {
  return (value * 100) / window.innerWidth;
};

/**
 * 일정 시간 동안 대기
 * @param delay 지연시간 (ms)
 * @returns Promise
 */
export const wait = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));

/**
 * 매장 문화권에 따라 금액 포맷 변환
 * @see 국가 코드가 지원하지 않을 경우, 한국 기준으로 표시한다
 * @param value
 */
export const formatCurrency = (value?: number) => {
  const configStore = useConfigStore();
  return new Intl.NumberFormat([configStore.config.CultureName, 'ko-KR']).format(value || 0);
};

/**
 * POS 언어코드를 locale 언어 코드로 변환
 * 지원하지 않는 언어 기본 값은 영어(en).
 * 서버 언어코드 목록은 기초코드 (LG 필드1번 참조)
 * @param posLanguage POS 언어코드
 * @returns locale 언어코드
 */
export const posToLocaleLanguage = (posLanguage: PosLanguage): LocaleLanguage => {
  switch (posLanguage) {
    case PosLanguage.Ko:
      return LocaleLanguage.Ko;
    case PosLanguage.Zh:
      return LocaleLanguage.Zh;
    case PosLanguage.Ja:
      return LocaleLanguage.Ja;
    case PosLanguage.Vi:
      return LocaleLanguage.Vi;
    default:
      return LocaleLanguage.En;
  }
};

/**
 * locale 언어코드를 POS 언어 코드로 변환
 * @param localeLanguage locale 언어코드
 * @returns POS 언어코드
 */
export const localeToPosLanguage = (locale: LocaleLanguage): PosLanguage => {
  switch (locale) {
    case LocaleLanguage.Ko:
      return PosLanguage.Ko;
    case LocaleLanguage.Ja:
      return PosLanguage.Ja;
    case LocaleLanguage.Zh:
      return PosLanguage.Zh;
    case LocaleLanguage.Vi:
      return PosLanguage.Vi;
    default:
      return PosLanguage.En;
  }
};

/**
 * 현재 설정된 locale에 따라 메뉴 이름을 반환
 * @param menuObject 메뉴 데이터
 * @returns locale이 적용된 메뉴 이름
 */
export const getNameByLocale = (
  menuObject?: ResponseItem | MenuGroup | Menu | ToppingGroup | Topping | SetMenuGroup | SetMenu,
) => {
  if (!menuObject) return '';
  let name = '';
  switch (i18n.global.locale.value) {
    case LocaleLanguage.Ko:
      name = menuObject.NameKo;
      break;
    case LocaleLanguage.En:
      name = menuObject.NameEn;
      break;
    case LocaleLanguage.Ja:
      name = menuObject.NameJa;
      break;
    case LocaleLanguage.Zh:
      name = menuObject.NameZh;
      break;
  }
  if (!name) name = menuObject.Name;

  return stripHTML(name);
};

/**
 * 현재 설정된 locale에 따라 메뉴 메모를 반환
 * @param menuItem 메뉴 데이터
 * @param oneLine 첫번째 줄의 텍스트만 리턴 여부
 * @param allowTag HTML 태그 허용 여부 (캐시노트 매장인 경우만)
 * @returns locale이 적용된 메뉴 이름
 */
export const getNoteByLocale = (menuItem: Menu | ResponseItem, oneLine = false, allowTag = false) => {
  // 캐시노트 매장인지 여부
  const configStore = useConfigStore();
  const isCashNote = configStore.config.ManageCompany === 'KPN' || configStore.config.ManageCompany === 'KCD';

  let note = '';
  switch (i18n.global.locale.value) {
    case LocaleLanguage.Ko:
      note = menuItem.MemoKo;
      break;
    case LocaleLanguage.En:
      note = menuItem.MemoEn;
      break;
    case LocaleLanguage.Ja:
      note = menuItem.MemoJa;
      break;
    case LocaleLanguage.Zh:
      note = menuItem.MemoZh;
      break;
  }
  if (!note) note = menuItem.Memo;

  // 태그 허용이 아니거나 일반 매장인 경우 태그 제거
  if (!allowTag || !isCashNote) note = stripHTML(note);

  // 한 줄 설명인 경우 첫번째 라인의 텍스트만 반환
  return oneLine ? note?.split('\n')[0] : note;
};

/**
 * 현재 설정된 locale에 따라 호출 메시지를 반환
 * @param callMessage 호출 메시지
 * @returns locale이 적용된 호출 메시지
 */
export const getCallMessageByLocale = (callMessage: CallMessage) => {
  let message = '';
  switch (i18n.global.locale.value) {
    case LocaleLanguage.Ko:
      message = callMessage.MessageKo;
      break;
    case LocaleLanguage.En:
      message = callMessage.MessageEn;
      break;
    case LocaleLanguage.Ja:
      message = callMessage.MessageJa;
      break;
    case LocaleLanguage.Zh:
      message = callMessage.MessageZh;
      break;
  }
  if (!message) message = callMessage.Message;
  return stripHTML(message);
};

export const definedProps = (obj: object) => {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null));
};

/**
 * 세금 계산
 * 일본 문화권인 경우 경감세율 옵션 포함하여 계산
 * @param totalPrice 메뉴 총 금액 (추가금액 있으면 합산)
 * @param isMenuTax1Used 메뉴 세금1 사용여부
 * @param isMenuTax2Used 메뉴 세금2 사용여부
 * @returns 세금
 */
export const getMenuTaxAmount = (totalPrice: number, isMenuTax1Used: boolean, isMenuTax2Used: boolean): number => {
  const configStore = useConfigStore();

  /**
   * 경감세금 사용여부 체크
   * 일본 문화권 && 홀서빙 경감세금 사용 && 메뉴 경감세금 사용 3가지 모두 충족 시 경감세금 적용
   */
  if (configStore.config.CultureName === 'ja-JP' && configStore.config.IsReduceTaxUsed && isMenuTax2Used) {
    const taxType2 = configStore.config.TaxType2 || '0';
    const taxPercentage2 = configStore.config.TaxPercentage2 || 0;

    if (taxPercentage2 === 0) return 0;

    // 세금 타입 체크 (0: 사용안함, 1: 세금별도, 2: 세금포함)
    if (taxType2 === '0') {
      // 사용안함
      return 0;
    } else if (taxType2 === '1') {
      // 세금별도
      return Math.round(totalPrice * (taxPercentage2 / 100));
    } else {
      // 세금포함
      const supplyPrice = Math.round(totalPrice / (1 + taxPercentage2 / 100));
      return totalPrice - supplyPrice;
    }
  } else if (isMenuTax1Used) {
    const taxType1 = configStore.config.TaxType1 || '0';
    const taxPercentage1 = configStore.config.TaxPercentage1 || 0;

    if (taxPercentage1 === 0) return 0;

    // 세금 타입 체크 (0: 사용안함, 1: 세금별도, 2: 세금포함)
    if (taxType1 === '0') {
      // 사용안함
      return 0;
    } else if (taxType1 === '1') {
      // 세금별도
      return Math.round(totalPrice * (taxPercentage1 / 100));
    } else {
      // 세금포함
      const supplyPrice = Math.round(totalPrice / (1 + taxPercentage1 / 100));
      return totalPrice - supplyPrice;
    }
  }

  return 0;
};

/**
 * .이 포함된 버전 문자열을 .을 제거하고 숫자로 변환하여 반환
 * @param version 버전 문자열 (xxx.xxx.xxx)
 * @returns 숫자로 변환된 버전
 */
export const convertVerionToNumber = (version: string): number => {
  return Number(version.replaceAll('.', ''));
};

/**
 * 현재 시간이 사용 가능한 시간인지 여부
 * @param useStartTime 사용 시작 시간
 * @param useEndTime 사용 종료 시간
 * @returns 사용 가능 여부
 */
export const checkUseTime = (useStartTime: string, useEndTime: string): boolean => {
  // 설정된 시간 값이 없으면 true 리턴
  if (!useStartTime || !useEndTime) return true;

  // 현재 시간 조회(HHmm)
  const currentTime = Number(dayjs().format('HHmm'));
  const startTime = Number(useStartTime);
  const endTime = Number(useEndTime === '0000' ? '2400' : useEndTime);

  if (startTime <= endTime) {
    // 시작시간이 종료시간보다 작거나 같은 경우 당일 기준으로 계산
    return currentTime >= startTime && currentTime <= endTime;
  } else {
    // 시작시간이 종료시간보다 큰경우 당일 ~ 다음날까지로 계산
    return currentTime >= startTime || currentTime <= endTime;
  }
};

/**
 * 이벤트 데이터를 Map으로 변환
 * @param events 이벤트 리스트
 * @param eventItems 이벤트 아이템 리스트
 * @returns 이벤트와 이벤트 리스트를 합친 Map
 */
export const generateEventMap = (
  events: ResponseEvent[],
  eventItems: ResponseEventItem[],
): Map<string, ResponseEvent & ResponseEventItem> => {
  const eventMap = new Map<string, ResponseEvent & ResponseEventItem>();
  const currentDate = dayjs();
  const currentTime = currentDate.format('HHmm');

  if (events.length === 0) return eventMap;
  if (eventItems.length === 0) return eventMap;

  // 행사 아이템을 Map으로 변환
  events.forEach((event) => {
    // 날짜 검사
    const startDate = dayjs(event.StartDate);
    const endDate = dayjs(event.EndDate);
    if (!currentDate.isBetween(startDate, endDate, 'day', '[]')) return;

    // 시간이 설정되어 있는 경우 시간 검사
    if (event.StartTime && event.EndTime) {
      if (currentTime < event.StartTime || currentTime >= event.EndTime) return;
    }

    // 해당 하는 이벤트 아이템
    eventItems.forEach((eventItem) => {
      if (event.EventCode === eventItem.EventCode) {
        // 중복되는 아이템코드가 있는 경우 덮어쓰기
        eventMap.set(eventItem.ItemCode, { ...event, ...eventItem });
      }
    });
  });

  return eventMap;
};

/**
 * HTML 태그가 포함된 문자열에서 태그를 제거하고 텍스트 문자열로 변환
 * @param html HTML 문자열
 * @returns 텍스트 문자열
 */
export const stripHTML = (html?: string): string => {
  const doc = new DOMParser().parseFromString(html ?? '', 'text/html');
  return doc.body.textContent || '';
};

/** 글로벌 에러 처리 */
export const handleGlobalError = (err: unknown, source = 'Unknown') => {
  const commonStore = useCommonStore();
  const error = err instanceof Error ? err : new Error(String(err));
  logger.error(`[${source}]`, error.stack ?? error.message);

  if (error instanceof UserError) {
    commonStore.showDialog({ type: 'alert', message: error.message });
  } else {
    commonStore.showDialog({ type: 'alert', message: i18n.global.t('error.common') });
  }
};

/** 쿠키에서 ASP 세션 삭제 */
export const deleteASPSESSIONIDCookies = () => {
  // 현재 도메인에서 설정된 모든 쿠키를 가져옴
  if (document.cookie) {
    const cookies = document.cookie.split(';');

    // 각 쿠키를 반복하면서 ASPSESSIONID로 시작하는지 확인하고 삭제
    cookies.forEach((cookie) => {
      const cookieName = cookie.split('=')[0].trim(); // 쿠키 이름
      if (cookieName.startsWith('ASPSESSIONID')) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      }
    });
  }
};
